import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Play, Copy } from 'lucide-react'
import { useAPIRequestStore, useAPIResponseStore, useRequestHistoryStore } from '../../stores/apiStore'
import { APIService } from '../../services/apiService'
import { API_ENVIRONMENT } from '../../config/environment'
import { useToast } from '../ui/ToastProvider'
import EndpointSelector from './EndpointSelector'
import HeadersEditor from './HeadersEditor'
import RequestBodyEditor from './RequestBodyEditor'
import QueryParamsEditor from './QueryParamsEditor'

const RequestPanel: React.FC = () => {
  const {
    selectedEndpoint,
    baseUrl,
    headers,
    requestBody,
    queryParams,
    viewMode,
    isLoading,
    setHeaders,
    setRequestBody,
    setQueryParams,
    setViewMode,
    setIsLoading,
    addHeader,
    removeHeader,
    addQueryParam,
    removeQueryParam
  } = useAPIRequestStore()

  const { setCurrentResponse, addResponse } = useAPIResponseStore()
  const { addToHistory } = useRequestHistoryStore()
  const { showSuccess, showError } = useToast()
  const [activeTab, setActiveTab] = useState<'params' | 'headers' | 'body'>('params')

  const apiService = APIService.getInstance()

  // Switch to valid tab when endpoint changes
  useEffect(() => {
    if (selectedEndpoint?.method === 'GET' && activeTab === 'body') {
      setActiveTab('params')
    }
  }, [selectedEndpoint?.method, activeTab])

  const handleSendRequest = async () => {
    if (!selectedEndpoint) return

    setIsLoading(true)
    
    try {
      // Create request object
      const request = {
        id: `req_${Date.now()}`,
        endpoint: selectedEndpoint,
        baseUrl,
        headers,
        requestBody,
        queryParams,
        timestamp: Date.now()
      }

      // Add to history
      addToHistory(request)

      // Make API call
      const response = await apiService.makeRequest(request)
      
      // Store response
      setCurrentResponse(response)
      addResponse(response)

    } catch (error) {
      console.error('Request failed:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleCopyAsCurl = async () => {
    if (!selectedEndpoint) {
      showError('Please select an endpoint first')
      return
    }

    try {
      const request = {
        endpoint: selectedEndpoint,
        baseUrl,
        headers,
        requestBody,
        queryParams
      }

      const curl = apiService.generateCurl(request, { includeHeaders: true, pretty: true })
      await navigator.clipboard.writeText(curl)
      showSuccess('cURL command copied to clipboard!')
    } catch (error) {
      showError('Failed to copy cURL command')
    }
  }

  const handleCopyUrl = async () => {
    if (!selectedEndpoint) {
      showError('Please select an endpoint first')
      return
    }

    try {
      // Build the full URL with query parameters
      const url = new URL(selectedEndpoint.path, baseUrl)
      Object.entries(queryParams).forEach(([key, value]) => {
        if (value) {
          url.searchParams.append(key, value)
        }
      })
      
      await navigator.clipboard.writeText(url.toString())
      showSuccess('Request URL copied to clipboard!')
    } catch (error) {
      showError('Failed to copy URL')
    }
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Request Configuration
          </h2>
          <button
            onClick={handleCopyAsCurl}
            className="p-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors"
            title="Copy as cURL"
          >
            <Copy className="w-4 h-4" />
          </button>
        </div>

        {/* Endpoint Selector */}
        <EndpointSelector />

        {/* Combined URL Row - Base URL, Method, and Path */}
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Request URL
            </label>
            {selectedEndpoint && (
              <button
                onClick={handleCopyUrl}
                className="flex items-center space-x-1 px-2 py-1 text-xs text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                title="Copy full URL"
              >
                <Copy className="w-3 h-3" />
                <span>Copy URL</span>
              </button>
            )}
          </div>
          <div className="flex items-center space-x-2">
            {/* HTTP Method Badge */}
            {selectedEndpoint && (
              <span className={`px-3 py-2 text-sm font-medium rounded-md border flex-shrink-0 ${
                selectedEndpoint.method === 'GET' ? 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900 dark:text-green-300 dark:border-green-700' :
                selectedEndpoint.method === 'POST' ? 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900 dark:text-blue-300 dark:border-blue-700' :
                'bg-gray-100 text-gray-800 border-gray-300 dark:bg-gray-600 dark:text-gray-300 dark:border-gray-500'
              }`}>
                {selectedEndpoint.method}
              </span>
            )}
            
            {/* Base URL Input */}
            <input
              type="text"
              value={baseUrl}
              readOnly
              className="w-48 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm cursor-not-allowed"
              placeholder="Enter base URL"
            />
            
            {/* Endpoint Path */}
            {selectedEndpoint && (
              <span className="px-3 py-2 bg-gray-50 dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-md text-sm text-gray-700 dark:text-gray-300 font-mono flex-shrink-0">
                {selectedEndpoint.path}
              </span>
            )}
          </div>
          
          {/* Development Proxy Indicator */}
          {selectedEndpoint && API_ENVIRONMENT.usingProxy && (
            <div className="mt-2 flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-xs text-green-700 dark:text-green-400">
                Development Mode: Using Vite proxy to avoid CORS issues
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex space-x-8 px-4">
          {[
            { key: 'params', label: 'Query Parameters' },
            { key: 'headers', label: 'Headers' },
            ...(selectedEndpoint?.method !== 'GET' ? [{ key: 'body', label: 'Body' }] : [])
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as any)}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.key
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'params' && (
          <QueryParamsEditor
            params={queryParams}
            onParamsChange={setQueryParams}
            onAddParam={addQueryParam}
            onRemoveParam={removeQueryParam}
            endpoint={selectedEndpoint}
          />
        )}
        
        {activeTab === 'headers' && (
          <HeadersEditor
            headers={headers}
            onHeadersChange={setHeaders}
            onAddHeader={addHeader}
            onRemoveHeader={removeHeader}
            endpoint={selectedEndpoint}
          />
        )}
        
        {activeTab === 'body' && selectedEndpoint?.method !== 'GET' && (
          <RequestBodyEditor
            endpoint={selectedEndpoint}
            body={requestBody}
            onBodyChange={setRequestBody}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
          />
        )}
      </div>

      {/* Send Button */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-700">
        <motion.button
          onClick={handleSendRequest}
          disabled={!selectedEndpoint || isLoading}
          className={`w-full flex items-center justify-center space-x-2 px-4 py-3 rounded-md font-medium transition-all ${
            !selectedEndpoint || isLoading
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-primary-600 text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2'
          }`}
          whileHover={!selectedEndpoint || isLoading ? {} : { scale: 1.02 }}
          whileTap={!selectedEndpoint || isLoading ? {} : { scale: 0.98 }}
        >
          {isLoading ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span>Sending Request...</span>
            </>
          ) : (
            <>
              <Play className="w-4 h-4" />
              <span>Send Request</span>
            </>
          )}
        </motion.button>
      </div>
    </div>
  )
}

export default RequestPanel
